/**
 * Lytning Service
 *
 * Handles checking for Lyt-ning subscriptions across a customer's stores.
 * If any store has subscriptionProduct === "Lyt-ning", the customer should
 * be redirected to use the Lytning platform instead.
 *
 * @author Generated by Claude Code
 * @date 2025-12-30
 */

import axios from "axios";

/**
 * Check if a customer has any store with Lyt-ning subscription
 *
 * Queries the SubscriptionManager API to retrieve all stores for a customer
 * and checks if any store has subscriptionProduct === "Lyt-ning".
 *
 * @param {string|number} mainCustomerID - The main customer ID
 * @returns {Promise<boolean>} Returns true if customer has Lyt-ning subscription, false otherwise
 * @throws {Error} If API call fails or mainCustomerID is invalid
 *
 * @example
 * // Check if customer 2538 has Lyt-ning subscription
 * const hasLytning = await LytningService.checkLytningSubscription(2538);
 * if (hasLytning) {
 *   // Logout and redirect to login with message
 * }
 */
export const LytningService = {
    /**
     * Check if the specific store being accessed is on Lyt-ning platform.
     * Only blocks access if THE SPECIFIC STORE has Lyt-ning subscription.
     * Non-Lyt-ning stores are always allowed.
     *
     * @param {string|number} mainCustomerID - The main customer ID
     * @param {string|number} customerId - The store's CustomerID to check
     * @returns {Promise<boolean>} Returns true if store is Lyt-ning (should be blocked), false otherwise
     */
    async isStoreLytning(mainCustomerID, customerId) {
        // Validate inputs
        if (!mainCustomerID || (typeof mainCustomerID === 'string' && !mainCustomerID.trim())) {
            console.warn('LytningService: Invalid mainCustomerID provided');
            return false;
        }
        if (!customerId && customerId !== 0) {
            console.warn('LytningService: Invalid customerId provided');
            return false;
        }

        try {
            // Make API call to SubscriptionManager
            const { data } = await axios.get(
                window.subscriptionApi + `license/customer/${mainCustomerID}`
            );

            // Check if response has stores array
            if (!data || !Array.isArray(data.stores)) {
                console.warn('LytningService: Invalid response format - no stores array');
                return false;
            }

            // Match by storeId (from API) with customerId (from frontend)
            const customerIdNum = Number(customerId);
            const isLytningStore = data.stores.some(store => {
                return (store.storeId === customerIdNum || store.storeId === customerId) &&
                       store.subscriptionProduct === "Lyt-ning";
            });

            console.log('LytningService: Store check complete', {
                mainCustomerID,
                customerId,
                isLytningStore
            });

            return isLytningStore;

        } catch (error) {
            console.error('LytningService: API call failed', {
                mainCustomerID,
                customerId,
                error: error.message
            });
            // Fail open - if we can't check, allow them to proceed
            return false;
        }
    },

    /**
     * Check if customer has ANY store with Lyt-ning subscription.
     * Used during app initialization to block access for Lytning customers.
     *
     * @param {string|number} mainCustomerID - The main customer ID
     * @returns {Promise<boolean>} Returns true if customer has any Lyt-ning store, false otherwise
     */
    async checkLytningSubscription(mainCustomerID) {
        if (!mainCustomerID || (typeof mainCustomerID === 'string' && !mainCustomerID.trim())) {
            console.warn('LytningService: Invalid mainCustomerID provided');
            return false;
        }

        try {
            const { data } = await axios.get(
                window.subscriptionApi + `license/customer/${mainCustomerID}`
            );

            if (!data || !Array.isArray(data.stores)) {
                console.warn('LytningService: Invalid response format - no stores array');
                return false;
            }

            // Check if ANY store has Lyt-ning subscription
            const hasLytning = data.stores.some(store => store.subscriptionProduct === "Lyt-ning");

            console.log('LytningService: Customer subscription check complete', {
                mainCustomerID,
                hasLytningSubscription: hasLytning
            });

            return hasLytning;

        } catch (error) {
            console.error('LytningService: Subscription check API call failed', {
                mainCustomerID,
                error: error.message
            });
            // Fail open - if we can't check, allow them to proceed
            return false;
        }
    }
};

export default LytningService;
